/**
 * Rider-oriented roughness: fast/deep suspension strokes + vertical impacts,
 * emphasizing sustained harsh sequences over isolated small bumps.
 * No GPS — idle = very low suspension activity only.
 */

import { buildSuspensionSeries } from './suspensionAnalysis';

const BIN_SECONDS = 0.5;
const MIN_ACTIVE_MINUTES = 0.25;

/** Perception plateaus above ~2.5 g — extra accel adds little to felt harshness. */
const ACCEL_PERCEPTION_KNEE_G = 2.5;
const ACCEL_PERCEPTION_CAP_G = 4.5;

/** Default anchors when admin has not calibrated (raw → display score). */
export const DEFAULT_ROUGHNESS_ANCHORS = [
  { raw: 0.12, score: 0 },
  { raw: 0.4, score: 50 },
  { raw: 0.75, score: 100 },
];

function median(arr) {
  const v = arr.filter((x) => x != null && Number.isFinite(x)).sort((a, b) => a - b);
  if (!v.length) return null;
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
}

function percentile(sortedArr, p) {
  if (!sortedArr.length) return null;
  const idx = (p / 100) * (sortedArr.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedArr[lo];
  return sortedArr[lo] + (sortedArr[hi] - sortedArr[lo]) * (idx - lo);
}

/** Saturating vertical accel for rider perception (g). */
export function perceivedAccelG(absG) {
  const g = Math.max(0, absG ?? 0);
  if (g <= ACCEL_PERCEPTION_KNEE_G) return g;
  const extra = g - ACCEL_PERCEPTION_KNEE_G;
  return ACCEL_PERCEPTION_KNEE_G + (ACCEL_PERCEPTION_CAP_G - ACCEL_PERCEPTION_KNEE_G) * (1 - Math.exp(-extra / 1.2));
}

function isSampleActive(velSum, travelDev, absG) {
  return velSum >= 100 || travelDev >= 0.07 || absG >= 1.0;
}

/**
 * @param {ReturnType<typeof buildSuspensionSeries>} series
 * @returns {{ raw: number, components: object } | null}
 */
export function computeRoughnessRaw(series) {
  if (!series?.times?.length) return null;

  const { times, frontMm, rearMm, frontVel, rearVel, accelZ, rangeFront, rangeRear } = series;
  const n = times.length;
  const medF = median(frontMm);
  const medR = median(rearMm);
  const scaleF = rangeFront > 0 ? rangeFront : 1;
  const scaleR = rangeRear > 0 ? rangeRear : 1;

  const harshness = [];
  const active = [];
  let activeDurationS = 0;

  for (let i = 0; i < n; i += 1) {
    const dt = i > 0 ? Math.max(0, times[i] - times[i - 1]) : 0;
    const fv = frontVel[i] != null ? Math.abs(frontVel[i]) : 0;
    const rv = rearVel[i] != null ? Math.abs(rearVel[i]) : 0;
    const velSum = fv + rv;
    const devF = frontMm[i] != null && medF != null ? Math.abs(frontMm[i] - medF) / scaleF : 0;
    const devR = rearMm[i] != null && medR != null ? Math.abs(rearMm[i] - medR) : 0;
    const travelDev = devF + devR;
    const absG = accelZ[i] != null ? Math.abs(accelZ[i]) : 0;

    const act = isSampleActive(velSum, travelDev, absG);
    active.push(act);
    if (act && dt > 0 && dt < 2) activeDurationS += dt;

    const velNorm = Math.min(1, velSum / 1100);
    const accNorm = Math.min(1, perceivedAccelG(absG) / ACCEL_PERCEPTION_CAP_G);
    const depthNorm = Math.min(1, travelDev / 0.4);
    harshness.push(0.34 * velNorm + 0.38 * accNorm + 0.28 * depthNorm);
  }

  const activeMinutes = activeDurationS / 60;
  if (activeMinutes < MIN_ACTIVE_MINUTES) {
    return {
      raw: 0,
      components: {
        activeMinutes,
        intensity: 0,
        sequenceRate: 0,
        sustainedFraction: 0,
        brutalBurden: 0,
      },
    };
  }

  const binEdges = [];
  const t0 = times[0];
  const tEnd = times[n - 1];
  for (let t = t0; t <= tEnd; t += BIN_SECONDS) binEdges.push(t);

  const binScores = [];
  const binActiveFlags = [];

  for (let b = 0; b < binEdges.length; b += 1) {
    const tStart = binEdges[b];
    const tStop = b + 1 < binEdges.length ? binEdges[b + 1] : tEnd + BIN_SECONDS;
    const scores = [];
    let actSamples = 0;
    let totalSamples = 0;

    for (let i = 0; i < n; i += 1) {
      if (times[i] < tStart || times[i] >= tStop) continue;
      totalSamples += 1;
      if (active[i]) actSamples += 1;
      scores.push(harshness[i]);
    }

    if (totalSamples < 2) continue;
    const fracActive = actSamples / totalSamples;
    if (fracActive < 0.25) continue;

    const sorted = [...scores].sort((a, c) => a - c);
    const p90 = percentile(sorted, 90) ?? 0;
    binScores.push(p90);
    binActiveFlags.push(true);
  }

  if (!binScores.length) {
    return {
      raw: 0,
      components: { activeMinutes, intensity: 0, sequenceRate: 0, sustainedFraction: 0, brutalBurden: 0 },
    };
  }

  const sortedBins = [...binScores].sort((a, c) => a - c);
  const seqThreshold = Math.max(0.22, percentile(sortedBins, 55) ?? 0.22);

  const sequences = [];
  let seqStart = null;
  let seqScores = [];
  let seqBins = 0;

  const flushSeq = () => {
    if (seqStart == null || !seqScores.length) return;
    const mean = seqScores.reduce((a, b) => a + b, 0) / seqScores.length;
    const peak = Math.max(...seqScores);
    const durationS = seqBins * BIN_SECONDS;
    if (durationS >= 0.4) {
      sequences.push({
        durationS,
        mean,
        peak,
        weight: durationS * (0.55 * mean + 0.45 * Math.min(peak, 0.95)),
      });
    }
    seqStart = null;
    seqScores = [];
    seqBins = 0;
  };

  for (let i = 0; i < binScores.length; i += 1) {
    if (binScores[i] >= seqThreshold) {
      if (seqStart == null) seqStart = i;
      seqScores.push(binScores[i]);
      seqBins += 1;
    } else {
      flushSeq();
    }
  }
  flushSeq();

  const intensity = percentile(sortedBins, 95) ?? 0;
  const significantSeqs = sequences.filter((s) => s.durationS >= 1);
  const sequenceRate = significantSeqs.length / activeMinutes;
  const sustainedFraction = sequences.reduce((s, q) => s + q.durationS, 0) / (activeMinutes * 60);
  const brutalBurden = sequences.reduce((s, q) => s + q.weight, 0) / activeMinutes;

  const sequenceNorm = Math.min(1, sequenceRate / 5);
  const burdenNorm = Math.min(1, brutalBurden / 7);
  const sustainedNorm = Math.min(1, sustainedFraction);

  const raw =
    0.32 * intensity +
    0.28 * sequenceNorm +
    0.22 * sustainedNorm +
    0.18 * burdenNorm;

  return {
    raw: Math.max(0, raw),
    components: {
      activeMinutes,
      intensity,
      sequenceRate,
      sustainedFraction,
      brutalBurden,
      sequenceCount: significantSeqs.length,
    },
  };
}

/**
 * Map raw composite to 0–200 display score using admin benchmark points.
 * @param {number} raw
 * @param {{ raw: number, score: number }[]} anchors — sorted internally; need ≥2 for custom curve
 */
export function mapRawToRoughnessScore(raw, anchors) {
  const r = Number.isFinite(raw) ? Math.max(0, raw) : 0;
  const points = (Array.isArray(anchors) && anchors.length >= 2 ? anchors : DEFAULT_ROUGHNESS_ANCHORS)
    .map((p) => ({ raw: Number(p.raw), score: Number(p.score) }))
    .filter((p) => Number.isFinite(p.raw) && Number.isFinite(p.score))
    .sort((a, b) => a.raw - b.raw);

  if (points.length < 2) {
    const t = DEFAULT_ROUGHNESS_ANCHORS;
    return interpolateAnchors(r, t);
  }

  return interpolateAnchors(r, points);
}

function interpolateAnchors(raw, points) {
  if (raw <= points[0].raw) {
    if (points[0].raw <= 0) return Math.max(0, Math.min(200, points[0].score));
    const slope = points[0].score / points[0].raw;
    return Math.max(0, Math.min(200, slope * raw));
  }

  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    if (raw <= b.raw) {
      const t = (raw - a.raw) / (b.raw - a.raw || 1);
      const score = a.score + t * (b.score - a.score);
      return Math.max(0, Math.min(200, score));
    }
  }

  const last = points[points.length - 1];
  const prev = points[points.length - 2];
  const segSlope = (last.score - prev.score) / (last.raw - prev.raw || 1);
  const extra = raw - last.raw;
  const score = last.score + extra * segSlope * 0.45;
  return Math.max(0, Math.min(200, score));
}

/**
 * Build anchors from admin benchmark entries ({ raw, assignedScore }).
 */
export function benchmarksToAnchors(benchmarks) {
  if (!Array.isArray(benchmarks) || benchmarks.length < 2) return DEFAULT_ROUGHNESS_ANCHORS;
  return benchmarks
    .map((b) => ({ raw: Number(b.raw), score: Number(b.assignedScore ?? b.score) }))
    .filter((p) => Number.isFinite(p.raw) && Number.isFinite(p.score))
    .sort((a, b) => a.raw - b.raw);
}

/**
 * @param {object[]} telemetryData
 * @param {{ benchmarks?: { raw: number, assignedScore: number }[] }} [calibration]
 */
export function computeRoughnessFromTelemetry(telemetryData, calibration = {}) {
  const series = buildSuspensionSeries(telemetryData);
  if (!series) return { score: null, raw: null, components: null };
  const rawResult = computeRoughnessRaw(series);
  if (!rawResult) return { score: null, raw: null, components: null };
  const anchors = benchmarksToAnchors(calibration.benchmarks);
  const score = mapRawToRoughnessScore(rawResult.raw, anchors);
  return { score, raw: rawResult.raw, components: rawResult.components };
}
